<template>
  <v-card>
      
    <!-- Main toolbar -->
    <v-toolbar :color="appColor_Light" dark flat>
    <span class="subtitle">
      <v-icon class="pr-2">format_list_numbered</v-icon>
      {{ getExpression('FormTitle', 'PlaylistPreview') }} - <i>{{ playlistName }}</i>
    </span>

      <v-spacer />

      <v-toolbar-items>
        <v-btn
          @click="close"
          large
          :color="appColor_Medium"
          class="elevation-0"
          >
        <span class="subheading font-weight-medium"> {{ getExpression('Button', 'Close') }} </span></v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <v-alert
      v-if="isAnnouncements"
      type="info"
      border="left"
      color="info"
      colored-border
      class="mx-2 mt-2 mb-0 pa-1 px-3 grey lighten-3"
      >
      <span class="body-2" v-html="getExpression('Alert', 'PlaylistPreviewDescription')"></span>
    </v-alert>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="previewBlobs"
      :loading="isLoading"
      :no-data-text="noDataText"
      :footer-props="{
          'items-per-page-options': [10]
        }"
      class="pa-1"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate></v-progress-linear>

      <template slot="item" slot-scope="data">
        <div class='my-border'>{{data.item.name}}</div>
      </template>

      <template v-slot:body="{ items }">
        <tbody>
          <tr :class="isAnnouncement(item.kioskFolder) ? 'announcement-row' : 'standard-row'" v-for="(item) in items" :key="item.name">
            <td>
                <router-link :to="{ name: currentRoute }">
                <span @click="preview(item)">
                  <v-img width="50" height="50" :src="getThumbnailUrl(item)"></v-img>
                </span>
                </router-link>
            </td>
            <td>
              <span class="text-left pl-0">
                <v-icon v-if="getContentTypeAbbr(item.contentType) === 'image'" large>photo</v-icon>
                <v-icon v-if="getContentTypeAbbr(item.contentType) === 'video'" large>movie</v-icon>
                <v-icon v-if="getContentTypeAbbr(item.contentType) === 'audio'" large>audiotrack</v-icon>
              </span>
            </td>
            <td><span :class="getRowFont(item)">{{ item.title }}</span></td>
            <td><span :class="getRowFont(item)">{{ item.description }}</span></td>
            <td><span :class="getRowFont(item)">{{ getFormattedDuration(item) }}</span></td>
          </tr>
        </tbody>
      </template>
    </v-data-table>

    <!-- Media Previewer -->
    <v-dialog
      v-model="previewDialog"
      max-width="800px"
      persistent
      >
      <media-previewer
        v-bind:type="previewType"
        v-bind:url="previewUrl"
        v-bind:title="previewTitle"
        v-on:close="closePreview"
        >
      </media-previewer>
    </v-dialog>

  </v-card>
</template>

<script>
import appService from '@/app.service'
import { mapGetters } from 'vuex'
import MediaPreviewer from '@/components/MediaPreviewer'
import { AppColor, ThumbnailUrl, Base64Image, KioskFolder } from '@/config.js'

export default {
  components: {
    MediaPreviewer
  },
  name: 'PlaylistPreviewer',
  props: {
    previewBlobs: {
      type: Array,
      default: null
    },
    kioskId: {
      type: Number,
      default: -1
    },
    playlistName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      // colors
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Progress: AppColor.Progress,

      // kiosk folders
      kioskFolder_Ambient: KioskFolder.Ambient,
      kioskFolder_Announcement: KioskFolder.Announcement,

      // uri
      thumbnailUrl_Genie: ThumbnailUrl.Genie,

      // icons
      base64Image_AudioIcon: Base64Image.AudioIcon,

      isLoading: false,
      previewDialog: false,
      previewType: null,
      previewTitle: null,
      previewUrl: null
    }
  },
  computed: {
    ...mapGetters(['currentRoute']),
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      return this.previewBlobs.length === 0
        ? this.getExpression('Alert', 'NoPreviewItems')
        : ''
    },
    isAnnouncements () {
      const self = this
      return this.previewBlobs
        .filter(pb => self.isAnnouncement(pb.kioskFolder)).length > 0
    },
    headers () {
      return [
        {
          value: 'thumbnail',
          align: 'center',
          width: '5%',
          sortable: false,
          class: "pr-0"
        },
        {
          value: 'contentType',
          align: 'left',
          width: '5%',
          sortable: false,
          class: "pr-0"
        },
        {
          text: this.getExpression('Label', 'Title'),
          value: 'title',
          align: 'left',
          width: '35%',
          sortable: false,
          class: "pr-0"
        },
        {
          text: this.getExpression('Label', 'Description'),
          value: 'description',
          align: 'left',
          width: '45%',
          sortable: false,
          class: "pr-0"
        },
        {
          text: this.getExpression('Label', 'Duration'),
          value: 'duration',
          align: 'left',
          width: '10%',
          sortable: false,
          class: "pr-0"
        }
      ]
    }
  },
  methods: {
    close () {
      this.$emit('close')
    },

    preview (item) {
      const self = this
      appService.getBlobUrl(item.containerName, item.blobName).then(data => {
        const mediaType = this.getMediaType(item.contentType)
        self.previewUrl = data
        self.previewType = mediaType
        self.previewTitle = item.title
        self.previewDialog = true
      }).catch(e => { self.handleCriticalError(e) })
    },

    closePreview () {
      this.previewType = null
      this.previewUrl = null
      this.previewDialog = false
    },

    getThumbnailUrl (item) {
      const mediaType = this.getMediaType(item.contentType)
      if (mediaType !== 'audio') {
        const blobName = item.blobName
        const ext = blobName.split('.').pop()
        const thumnailName = blobName.replace(ext, 'jpg')

        const url = `${this.thumbnailUrl_Genie}/${item.containerName}/${thumnailName}`
        item.thumbnail = url

        return url
      } else {
        const url = this.base64Image_AudioIcon
        item.thumbnail = url

        return url
      }
    },
    
    getFormattedDuration (item) {
      const duration = item.duration
      
      var hours = Math.floor(duration / 3600)
      var minutes = Math.floor((duration - (hours * 3600)) / 60)
      var seconds = duration - (hours * 3600) - (minutes * 60)

      if (hours < 10) { hours = "0" + hours }
      if (minutes < 10) { minutes = "0" + minutes }
      if (seconds < 10) { seconds = "0" + seconds }

      return hours + ':' + minutes + ':' + seconds;
    },

    getRowFont (item) {
      return this.isAnnouncement(item)
        ? 'text-left font-weight-medium'
        : 'text-left'
    },

    isAnnouncement (kioskFolder) {
      return kioskFolder === this.kioskFolder_Announcement
    }
  }
}
</script>

<style scoped>
.standard-row{
  background-color: rgb(228, 244, 250);
}
.announcement-row{
  background-color: rgb(255, 255, 144);
}
</style>